/**
 * Aspect — Tour panel (rooms list, auto-tour, 3D room labels, measure readout)
 * --------------------------------------------------------------
 * DOM-only UI companion for TourCamera + MeasureTool (engine modules stay
 * DOM-free; this module renders their events):
 *
 *   - "Tour" button      — starts/stops the auto-tour
 *   - Rooms dropdown     — click a room to glide there
 *   - Labels toggle      — floating name + m² chips over each room centroid
 *   - Measure toggle     — tape-measure mode (M) with a live distance chip
 *   - Tour banner        — current room name + area during the auto-tour
 */

import * as pc from "playcanvas";
import { roomCenterXZ, roomDisplay } from "../interaction/tour_camera.js";
import { finishOptions, DEFAULT_WALL_FINISH, DEFAULT_FLOOR_FINISH, DEFAULT_WET_FLOOR_FINISH } from "../scene/texture_sets.js";
import { ORCH_URL } from "../plan_analyzer.js";

const THEME_PRESETS = [
  { id: "scandinavian", label: "Scandinavian" },
  { id: "modern", label: "Modern" },
  { id: "classic", label: "Classic" },
];

const FINISH_GROUPS = [
  { role: "wall", label: "Walls", fallback: DEFAULT_WALL_FINISH },
  { role: "floor", label: "Floors", fallback: DEFAULT_FLOOR_FINISH },
  { role: "floorWet", label: "Wet rooms", fallback: DEFAULT_WET_FLOOR_FINISH, options: "floor" },
];

const tmpWorld = new pc.Vec3();
const tmpScreen = new pc.Vec3();

export class TourPanel {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {HTMLElement} mount overlay mount (DOM lives only in ui modules)
   * @param {{ tour: import('../interaction/tour_camera.js').TourCamera,
   *           measure: import('../interaction/measure_tool.js').MeasureTool,
   *           scene: object, camera: import('playcanvas').Entity }} deps
   */
  constructor(app, mount, { tour, measure, scene, camera, readOnly = false }) {
    this.app = app;
    this.tour = tour;
    this.measure = measure;
    this.scene = scene;
    this.camera = camera;
    this.readOnly = readOnly;
    this._labelsOn = false;
    this._labelEls = [];
    this._menuOpen = false;
    this._finishesOpen = false;

    this.root = document.createElement("div");
    // Below the main overlay layer (z-10) so the Floor picker panel stacks above
    // Rooms / Finishes menus when both would otherwise overlap.
    this.root.className = "pointer-events-none fixed inset-0 z-[8] select-none";
    this.root.innerHTML = `
      <div class="pointer-events-auto absolute right-3 top-[3.75rem] z-20 flex flex-col items-end gap-1.5" data-tour="panel">
        <div class="flex items-center gap-1.5">
          <button type="button" data-tour="share" class="hud-chip" title="Publish a read-only tour link">Share</button>
          <button type="button" data-tour="shot" class="hud-chip" title="Save a screenshot">Photo</button>
          <button type="button" data-tour="finishes-btn" class="hud-chip" title="Wall and floor finishes">Finishes ▾</button>
          <button type="button" data-tour="labels" class="hud-chip" title="Room name labels">Labels</button>
          <button type="button" data-tour="measure" class="hud-chip" title="Tape measure (M)">Measure</button>
          <button type="button" data-tour="rooms-btn" class="hud-chip" title="Go to room">Rooms ▾</button>
          <button type="button" data-tour="play" class="hud-chip hud-chip--accent" title="Auto tour of every room">▶ Tour</button>
        </div>
        <div data-tour="rooms-menu" class="hud-panel hidden max-h-64 w-44 overflow-y-auto p-1"></div>
        <div data-tour="finishes-menu" class="hud-panel hidden max-h-80 w-52 overflow-y-auto p-1"></div>
      </div>
      <div data-tour="banner" class="pointer-events-none absolute inset-x-0 bottom-16 z-20 hidden text-center">
        <span class="hud-panel inline-block px-4 py-1.5 text-sm font-semibold" data-tour="banner-text"></span>
      </div>
      <div data-tour="measure-chip" class="pointer-events-none fixed z-30 hidden -translate-x-1/2 -translate-y-full">
        <span class="hud-panel inline-block px-2 py-0.5 text-xs font-semibold" data-tour="measure-text"></span>
      </div>
      <div data-tour="labels-layer" class="pointer-events-none fixed inset-0 z-10"></div>
    `;
    mount.appendChild(this.root);

    this.panelEl = this.root.querySelector('[data-tour="panel"]');
    this.shareBtn = this.root.querySelector('[data-tour="share"]');
    this.shotBtn = this.root.querySelector('[data-tour="shot"]');
    this.playBtn = this.root.querySelector('[data-tour="play"]');
    this.roomsBtn = this.root.querySelector('[data-tour="rooms-btn"]');
    this.roomsMenu = this.root.querySelector('[data-tour="rooms-menu"]');
    this.finishesBtn = this.root.querySelector('[data-tour="finishes-btn"]');
    this.finishesMenu = this.root.querySelector('[data-tour="finishes-menu"]');
    this.labelsBtn = this.root.querySelector('[data-tour="labels"]');
    this.measureBtn = this.root.querySelector('[data-tour="measure"]');
    this.bannerEl = this.root.querySelector('[data-tour="banner"]');
    this.bannerText = this.root.querySelector('[data-tour="banner-text"]');
    this.measureChip = this.root.querySelector('[data-tour="measure-chip"]');
    this.measureText = this.root.querySelector('[data-tour="measure-text"]');
    this.labelsLayer = this.root.querySelector('[data-tour="labels-layer"]');

    // Publishing a share link only makes sense from the live editing client.
    if (this.readOnly) this.shareBtn.classList.add("hidden");
    else this.shareBtn.addEventListener("click", () => this._share());
    this.shotBtn.addEventListener("click", () => this._screenshot());

    this.playBtn.addEventListener("click", () => {
      if (this.tour.active) this.tour.stop();
      else this.tour.startTour();
    });
    this.roomsBtn.addEventListener("click", () => this._toggleMenu());
    this.finishesBtn.addEventListener("click", () => this._toggleFinishes());
    this.labelsBtn.addEventListener("click", () => this._setLabels(!this._labelsOn));
    this.measureBtn.addEventListener("click", () => this.measure.toggle());

    app.on("tour:rooms-changed", () => this._renderMenu());
    app.on("scene:room-loaded", () => {
      this._renderMenu();
      this._rebuildLabelEls();
    });
    app.on("tour:state", (s) => this._onTourState(s));
    app.on("measure:state", ({ active }) => {
      this.measureBtn.classList.toggle("hud-chip--accent", active);
      if (!active) this.measureChip.classList.add("hidden");
    });
    app.on("measure:changed", (m) => this._onMeasure(m));
    app.on("scene:finishes-changed", () => {
      if (this._finishesOpen) this._renderFinishes();
    });
    // Floor picker lives in the main overlay layer — close Rooms/Finishes when it opens.
    app.on("ui:floor-menu", ({ open }) => {
      if (open) {
        this._toggleMenu(false);
        this._toggleFinishes(false);
      }
    });
    app.on("update", () => this._tickLabels());
  }

  _toggleFinishes(force = null) {
    this._finishesOpen = force ?? !this._finishesOpen;
    this.finishesMenu.classList.toggle("hidden", !this._finishesOpen);
    this.finishesBtn.classList.toggle("hud-chip--accent", this._finishesOpen);
    if (this._finishesOpen) {
      this._toggleMenu(false);
      this.app.fire("ui:tour-menu", { open: true, source: "finishes" });
      this._renderFinishes();
    }
  }

  /** Publish the current scene as a tour snapshot and copy the link. */
  async _share() {
    const btn = this.shareBtn;
    btn.disabled = true;
    btn.textContent = "Publishing…";
    try {
      const res = await fetch(`${ORCH_URL}/tours`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const url = `${location.origin}${location.pathname}?tour=${encodeURIComponent(data.id)}`;
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        /* clipboard can be blocked (permissions/insecure context) */
      }
      console.info("[tour] share link:", url);
      btn.textContent = copied ? "Link copied!" : "Link in console";
    } catch (err) {
      console.warn("[tour] share failed", err);
      btn.textContent = "Share failed";
    } finally {
      btn.disabled = false;
      window.setTimeout(() => (btn.textContent = "Share"), 2500);
    }
  }

  /**
   * Save a PNG of the next rendered frame. Capturing inside "frameend" keeps
   * us in the same task as the draw, so the WebGL backbuffer is still valid
   * without preserveDrawingBuffer.
   */
  _screenshot() {
    const canvas = this.app.graphicsDevice?.canvas;
    if (!canvas) return;
    this.app.once("frameend", () => {
      try {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `aspect-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-")}.png`;
        a.click();
      } catch (err) {
        console.warn("[tour] screenshot failed", err);
      }
    });
    this.app.renderNextFrame = true;
  }

  async _applyTheme(themeId, btn) {
    btn.disabled = true;
    btn.textContent = "Applying…";
    try {
      this.app.fire("scene:sandbox-expect", {});
      const res = await fetch(`${ORCH_URL}/apply-theme`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          theme: themeId,
          snapshot: {
            placements: typeof this.scene.exportAdminPlacements === "function"
              ? this.scene.exportAdminPlacements()
              : [],
            room: this.scene.room ?? null,
            finishes: this.scene.room?.finishes ?? null,
          },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      if (data?.snapshot) this.app.fire("scene:http-resync", data.snapshot);
    } catch (err) {
      console.warn("[tour] theme apply failed", err);
    } finally {
      if (this._finishesOpen) this._renderFinishes();
    }
  }

  _renderFinishes() {
    const current = this.scene.room?.finishes ?? {};
    this.finishesMenu.innerHTML = "";

    // Themes restage the authoritative scene server-side — live clients only.
    if (!this.readOnly) {
      const themeHead = document.createElement("div");
      themeHead.className = "px-2 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-60";
      themeHead.textContent = "Style themes";
      this.finishesMenu.appendChild(themeHead);
      for (const t of THEME_PRESETS) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "block w-full rounded px-2 py-1 text-left text-xs font-semibold hover:bg-white/10";
        btn.textContent = `✦ ${t.label}`;
        btn.addEventListener("click", () => this._applyTheme(t.id, btn));
        this.finishesMenu.appendChild(btn);
      }
    }

    for (const group of FINISH_GROUPS) {
      const head = document.createElement("div");
      head.className = "px-2 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-60";
      head.textContent = group.label;
      this.finishesMenu.appendChild(head);
      const active = current[group.role] ?? group.fallback;
      for (const opt of finishOptions(group.options ?? group.role)) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `block w-full rounded px-2 py-1 text-left text-xs hover:bg-white/10${opt.id === active ? " bg-white/15 font-semibold" : ""}`;
        btn.textContent = opt.id === active ? `● ${opt.label}` : opt.label;
        btn.addEventListener("click", () => {
          this.app.fire("ui:set-finish", { role: group.role, finishId: opt.id });
        });
        this.finishesMenu.appendChild(btn);
      }
    }
  }

  _toggleMenu(force = null) {
    this._menuOpen = force ?? !this._menuOpen;
    this.roomsMenu.classList.toggle("hidden", !this._menuOpen);
    if (this._menuOpen) {
      this._toggleFinishes(false);
      this.app.fire("ui:tour-menu", { open: true, source: "rooms" });
      this._renderMenu();
    }
  }

  _renderMenu() {
    const rooms = this.tour.rooms();
    this.roomsMenu.innerHTML = "";
    if (!rooms.length) {
      const empty = document.createElement("div");
      empty.className = "px-2 py-1 text-xs opacity-60";
      empty.textContent = "No named rooms";
      this.roomsMenu.appendChild(empty);
      return;
    }
    for (const r of rooms) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "block w-full rounded px-2 py-1 text-left text-xs hover:bg-white/10";
      btn.textContent = r.area != null ? `${r.name} · ${r.area} m²` : r.name;
      btn.addEventListener("click", () => {
        this._toggleMenu(false);
        this.tour.goToRoom(r.index);
      });
      this.roomsMenu.appendChild(btn);
    }
  }

  _onTourState({ active, auto, label, area }) {
    this.playBtn.textContent = active && auto ? "■ Stop" : "▶ Tour";
    if (active && label) {
      this.bannerText.textContent = area != null ? `${label} — ${area} m²` : label;
      this.bannerEl.classList.remove("hidden");
    } else {
      this.bannerEl.classList.add("hidden");
    }
  }

  _onMeasure({ meters, screen, fixed }) {
    if (meters == null || !screen) {
      this.measureChip.classList.add("hidden");
      return;
    }
    const cm = meters < 1;
    this.measureText.textContent = cm ? `${Math.round(meters * 100)} cm` : `${meters.toFixed(2)} m`;
    this.measureText.style.opacity = fixed ? "1" : "0.75";
    this.measureChip.style.left = `${screen.x}px`;
    this.measureChip.style.top = `${screen.y - 10}px`;
    this.measureChip.classList.remove("hidden");
  }

  // --- 3D room labels (projected chips) --------------------------------------

  _setLabels(on) {
    this._labelsOn = Boolean(on);
    this.labelsBtn.classList.toggle("hud-chip--accent", this._labelsOn);
    if (this._labelsOn) this._rebuildLabelEls();
    else this._clearLabelEls();
  }

  _clearLabelEls() {
    for (const { el } of this._labelEls) el.remove();
    this._labelEls = [];
  }

  _rebuildLabelEls() {
    this._clearLabelEls();
    if (!this._labelsOn) return;
    const room = this.scene.room;
    const list = Array.isArray(room?.rooms) ? room.rooms : [];
    const floorY = room?.floorY ?? 0;
    list.forEach((entry, index) => {
      const { name, area } = roomDisplay(entry, index);
      const [cx, cz] = roomCenterXZ(entry);
      if (!Number.isFinite(cx) || !Number.isFinite(cz)) return;
      const el = document.createElement("span");
      el.className =
        "hud-panel absolute inline-block -translate-x-1/2 -translate-y-1/2 whitespace-nowrap px-2 py-0.5 text-[11px] font-semibold";
      el.textContent = area != null ? `${name} · ${area} m²` : name;
      this.labelsLayer.appendChild(el);
      this._labelEls.push({ el, x: cx, y: floorY + 1.9, z: cz });
    });
  }

  _tickLabels() {
    if (!this._labelsOn || !this._labelEls.length) return;
    const cam = this.camera?.camera;
    if (!cam) return;
    for (const rec of this._labelEls) {
      tmpWorld.set(rec.x, rec.y, rec.z);
      const sv = cam.worldToScreen(tmpWorld, tmpScreen);
      const visible = sv.z > 0;
      rec.el.style.display = visible ? "" : "none";
      if (visible) {
        rec.el.style.left = `${sv.x}px`;
        rec.el.style.top = `${sv.y}px`;
      }
    }
  }

  destroy() {
    this._clearLabelEls();
    this.root.remove();
  }
}

export default TourPanel;
